<template>
  <div class="home-page">
    <div class="home-page__layout">
      <main class="home-page__main">
        <h1 class="home-page__greeting">
          Bem-vindo(a)<template v-if="userName">, {{ userName }}</template>.
        </h1>

        <div
          v-if="showPendingCard"
          class="pending-card pending-card--success">
          <div class="pending-card__header">
            <div class="pending-card__icon pending-card__icon--success" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round">
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
                <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
              </svg>
            </div>
            <h2 class="pending-card__title">Tudo pronto pra começar!</h2>
            <p class="pending-card__text">
              Conecte seu CRM, conheça os planos ou fale com um vendedor pra escalar o time.
            </p>
          </div>
          <div class="pending-card__actions">
            <router-link
              v-if="isAdmin"
              :to="{ name: 'integrationManager' }"
              class="pending-card__btn pending-card__btn--primary">
              Integrações
            </router-link>
            <router-link
              v-if="isAdmin"
              :to="{ name: 'subscription' }"
              class="pending-card__btn pending-card__btn--secondary">
              Conhecer planos
            </router-link>
            <a
              v-if="isAdmin"
              :href="salesContactUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="pending-card__btn pending-card__btn--secondary">
              Falar com vendedor
            </a>
          </div>
        </div>
      </main>

      <aside class="home-page__sidebar">
        <!-- Card 1: Vendas é coletivo (apenas para o admin que se cadastrou) -->
        <div v-if="isFounder" class="home-card">
          <div class="home-card__header">
            <span class="home-card__icon"><i class="ik ik-users" /></span>
            <h4 class="home-card__title">Vendas é coletivo</h4>
          </div>
          <p class="home-card__text">
            Convide seus vendedores para começarem com você — eles já chegam prontos para ligar.
          </p>
          <button
            type="button"
            class="home-card__cta"
            @click="openAddUserModal">
            Enviar convites
          </button>
        </div>

        <!-- Card 2: Recursos para você -->
        <div class="home-card">
          <div class="home-card__header">
            <h4 class="home-card__title">Recursos para você</h4>
          </div>
          <ul class="home-card__resources">
            <li
              v-for="resource in resources"
              :key="resource.url">
              <a
                :href="resource.url"
                target="_blank"
                rel="noopener noreferrer">
                <i class="ik ik-external-link" />
                <span>{{ resource.title }}</span>
              </a>
            </li>
          </ul>
        </div>
      </aside>
    </div>

    <add-user-modal
      :visible="showInviteModal"
      :is-unlimited="isUnlimited"
      :pricing-list="pricingList"
      @close="showInviteModal = false" />

    <user-limit-reached-modal
      :visible="showUserLimitReachedModal"
      :max-active-users="inviteEligibilityMaxUsers !== null ? inviteEligibilityMaxUsers : 0"
      :message="inviteEligibilityBlockMessage"
      @close="closeUserLimitReachedModal"
      @upgrade-click="handleUserLimitUpgradeClick" />

    <account-restricted-modal
      :visible="showAccountRestrictedModal"
      @close="showAccountRestrictedModal = false" />
  </div>
</template>

<script>
import AddUserModal from '@/components/Users/AddUserModal'
import UserLimitReachedModal from '@/components/Users/UserLimitReachedModal'
import AccountRestrictedModal from '@/components/Users/AccountRestrictedModal'
import { loadAndMapOffers } from '@/services/pricing-offers'
import { getInviteEligibility, resolveInviteEligibilityMaxUsers, isAccountRestricted } from '@/services/invite-eligibility'
import { triggerSuccessConfetti } from '@/utils/celebration'
import { WHATSAPP_SALES_WEBHOOK_URL } from '@/utils/whatsapp'

export default {
  name: 'Home',
  components: { AddUserModal, UserLimitReachedModal, AccountRestrictedModal },
  data () {
    return {
      showInviteModal: false,
      showUserLimitReachedModal: false,
      showAccountRestrictedModal: false,
      inviteEligibilityBlockMessage: '',
      inviteEligibilityMaxUsers: null,
      pricingList: [],
      salesContactUrl: WHATSAPP_SALES_WEBHOOK_URL,
      resources: [
        {
          title: 'Como extrair o máximo da API4COM',
          url: 'https://ajuda.sacflow.io/api4com/articles/201-como-extrair-o-maximo-da-api4com'
        },
        {
          title: 'Diagnóstico gratuito da maturidade do time comercial',
          url: 'https://ajuda.sacflow.io/api4com/articles/246-diagnostico-gratuito-maturidade-do-time-comercial'
        },
        {
          title: 'Guia prático de vendas',
          url: 'https://api4com.notion.site/guia-pratico-de-vendas'
        }
      ]
    }
  },
  created () {
    loadAndMapOffers().then(list => {
      this.pricingList = list
    }).catch(() => { /* falha silenciosa: modal abre com lista vazia */ })
  },
  mounted () {
    this.consumeCelebrationFlag()
  },
  computed: {
    userName () {
      return this.$store.state.auth.user && this.$store.state.auth.user.name
    },
    isUnlimited () {
      const org = this.$store.state.auth.organization
      return !!(org && org.isUnlimited === true)
    },
    isAdmin () {
      const user = this.$store.state.auth.user
      return !!(user && user.role === 'ADMIN')
    },
    isFounder () {
      const user = this.$store.state.auth.user
      const org = this.$store.state.auth.organization
      return !!(this.isAdmin && user && user.email && org && user.email === org.email)
    },
    showPendingCard () {
      return this.isFounder
    }
  },
  methods: {
    async openAddUserModal () {
      this.inviteEligibilityBlockMessage = ''
      const eligibility = await this.fetchInviteEligibility(true)
      if (isAccountRestricted(eligibility)) {
        this.showAccountRestrictedModal = true
        return
      }
      if (this.isUnlimited) {
        this.showInviteModal = true
        return
      }
      if (eligibility === null) {
        this.inviteEligibilityBlockMessage = 'Não foi possível validar a elegibilidade para convite agora. Tente novamente em instantes.'
        this.showUserLimitReachedModal = true
        return
      }
      if (eligibility.canInvite !== true) {
        this.showUserLimitReachedModal = true
        return
      }
      this.showInviteModal = true
    },
    closeUserLimitReachedModal () {
      this.showUserLimitReachedModal = false
      this.inviteEligibilityBlockMessage = ''
    },
    handleUserLimitUpgradeClick () {
      this.closeUserLimitReachedModal()
      this.$router.push('/subscription')
    },
    async fetchInviteEligibility (forceRefresh = false) {
      const eligibility = await getInviteEligibility({ forceRefresh })
      if (!eligibility) return null
      this.inviteEligibilityMaxUsers = resolveInviteEligibilityMaxUsers(eligibility)
      return eligibility
    },
    consumeCelebrationFlag () {
      const user = this.$store.state.auth.user
      if (!user || !user.uuid) return
      const key = `celebration-pending:${user.uuid}`
      try {
        if (!window.localStorage.getItem(key)) return
        window.localStorage.removeItem(key)
      } catch (_) {
        return
      }
      triggerSuccessConfetti()
    }
  }
}
</script>

<style lang="scss" scoped>
.home-page {
  padding: 24px;

  &__layout {
    display: grid;
    gap: 24px;
    grid-template-columns: 1fr 300px;
    max-width: 1280px;
    margin: 0 auto;

    @media (max-width: 991px) {
      grid-template-columns: 1fr;
    }
  }

  &__main {
    min-width: 0;
  }

  &__greeting {
    font-size: 1.75rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 24px;
  }

  &__sidebar {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.pending-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  padding: 32px;
  text-align: center;
  transition: background 0.35s ease, border-color 0.35s ease;

  &--success {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.06) 0%, rgba(37, 99, 235, 0.04) 100%);
    border-color: rgba(34, 197, 94, 0.25);
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 9999px;
    background: rgba(37, 99, 235, 0.12);
    color: #2563EB;
    margin-bottom: 16px;

    &--success {
      background: rgba(34, 197, 94, 0.15);
      color: #16a34a;
    }
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px;
  }

  &__text {
    font-size: 0.95rem;
    color: #6b7280;
    line-height: 1.5;
    margin: 0 0 24px;
  }

  &__actions {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 20px;
    font-size: 0.9rem;
    font-weight: 600;
    border-radius: 8px;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &--primary {
      background: #2563EB;
      color: #ffffff;
      border: 1px solid #2563EB;

      &:hover {
        background: #1D4ED8;
        color: #ffffff;
        text-decoration: none;
      }
    }

    &--secondary {
      background: #ffffff;
      color: #2563EB;
      border: 1px solid rgba(37, 99, 235, 0.35);

      &:hover {
        background: rgba(37, 99, 235, 0.06);
        color: #2563EB;
        text-decoration: none;
      }
    }
  }
}

.home-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    background: rgba(37, 99, 235, 0.12);
    color: #2563EB;
    font-size: 0.85rem;
  }

  &__title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  &__text {
    font-size: 0.8rem;
    color: #6b7280;
    line-height: 1.4;
    margin: 0;

    &--muted {
      color: #9ca3af;
    }
  }

  &__resources {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      border-top: 1px solid #f3f4f6;

      &:first-child {
        border-top: none;
      }
    }

    a {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      font-size: 0.8rem;
      color: #374151;
      text-decoration: none;

      i {
        color: #9ca3af;
        font-size: 0.85rem;
        flex-shrink: 0;
      }

      &:hover {
        color: #2563EB;
        text-decoration: none;

        i {
          color: #2563EB;
        }
      }
    }
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 12px;
    padding: 7px 12px;
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 500;
    color: #2563EB;
    background: #ffffff;
    border: 1px solid rgba(37, 99, 235, 0.35);
    border-radius: 6px;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background: rgba(37, 99, 235, 0.06);
      color: #2563EB;
      text-decoration: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Home.vue